import Linkify from 'linkify-react';
import * as React from 'react';
import styled from 'styled-components';

import * as colors from '../../../shared/colors';
import LikertPreview from '../../../shared/components/LikertPreview';
import SliderPreview from '../../../shared/components/SliderPreview';
import t from '../../../shared/translations';
import { IPollAnswerSet, IQuizAnswerSet, ISharedAnswer } from '../query';
import { Box, CheckboxAnswer, FileLogo, TextboxAnswer } from './stylings';

interface IProps {
    sharedAnswers: ISharedAnswer[];
    openQuizResults?: (quizAnswerSet: IQuizAnswerSet) => void;
    openPollResults?: (pollAnswerSet: IPollAnswerSet) => void;
}

const Hr = styled.hr`
    border-color: ${colors.primaryColor};
`;

// Supported answer types: textbox, checkbox, quiz, poll, file

function UngroupedAnswers({ sharedAnswers, openQuizResults, openPollResults }: IProps): JSX.Element {
    return <>
        {sharedAnswers.map(sharedAnswer => {
            const name = !!sharedAnswer.taskGroups
                ? sharedAnswer.taskGroups.join(', ')
                : !!sharedAnswer.people
                    ? sharedAnswer.people.map(person => `${person.firstName} ${person.lastName}`).join(', ')
                    : '';
            const count = sharedAnswer.taskAnswers.length;
            return <Box key={sharedAnswer.id}>
                {name && <h4>{t('player.submitted-task.heading-all-answers-submitted-answer', { count, name })}</h4>}
                {sharedAnswer.taskAnswers.map((answer, index) => {
                    const last = index + 1 === sharedAnswer.taskAnswers.length;
                    if (answer.answerType === 'textbox') {
                        return <div key={answer.id}>
                            {answer.questionContent && <h5>{answer.questionContent}</h5>}
                            <TextboxAnswer>
                                <Linkify options={{ nl2br: true }} tagName='p'>{answer.answerContent}</Linkify>
                            </TextboxAnswer>
                            {!last && <Hr />}
                        </div>;
                    }
                    if (answer.answerType === 'checkbox') {
                        return <div key={answer.id}>
                            {answer.questionContent && <h5>{answer.questionContent}</h5>}
                            {answer.checkboxAnswers.map(ans => (
                                <CheckboxAnswer $checked={ans.checked} key={ans.id}>
                                    <input type='checkbox' checked={ans.checked} disabled />
                                    {' '}{ans.content}
                                </CheckboxAnswer>
                            ))}
                            {!last && <Hr />}
                        </div>;
                    }
                    if (answer.answerType === 'likert') {
                        return <div key={answer.id}>
                            {answer.questionContent && <h5>{answer.questionContent}</h5>}
                            {answer.taskLikertAnswers.map(ans => (
                                <LikertPreview
                                    statement={ans.statement}
                                    leftLabel={ans.leftLabel}
                                    rightLabel={ans.rightLabel}
                                    selected={ans.value}
                                />
                            ))}
                            {!last && <Hr />}
                        </div>;
                    }
                    if (answer.answerType === 'impact-tracker') {
                        return <div key={answer.id}>
                            {answer.questionContent && <h5>{answer.questionContent}</h5>}
                            {answer.impactTrackerAnswerSet.impactLikertAnswers.map(ans => (
                                <SliderPreview
                                    statement={ans.statement}
                                    leftLabel={ans.leftLabel}
                                    rightLabel={ans.rightLabel}
                                    value={ans.value}
                                    showValue
                                />
                            ))}
                            {!last && <Hr />}
                        </div>;
                    }
                    if (answer.answerType === 'quiz') {
                        const [correct, total] = answer.quizAnswerSet?.results ?? [0, 0];
                        return <div key={answer.id}>
                            {answer.questionContent && <h5>{answer.questionContent}</h5>}
                            <span>{t('player.submitted-task.info-all-answers-quiz-result', { correct, total })}</span>
                            {!!openQuizResults && <>
                                {' '}
                                <button onClick={() => openQuizResults(answer.quizAnswerSet)} type='button'>
                                    {t('player.submitted-task.button-quiz-results')}
                                </button>
                            </>}
                            {!last && <Hr />}
                        </div>;
                    }
                    if (answer.answerType === 'poll') {
                        return <div key={answer.id}>
                            {answer.questionContent && <h5>{answer.questionContent}</h5>}
                            <span>{t('player.submitted-task.info-all-answers-poll')}</span>
                            {!!openPollResults && answer.showPollResults && <>
                                {' '}
                                <button onClick={() => openPollResults(answer.pollAnswerSet)} type='button'>
                                    {t('player.submitted-task.button-poll-results')}
                                </button>
                            </>}
                            {!last && <Hr />}
                        </div>;
                    }
                    if (answer.answerType === 'file') {
                        const taskAnswerFile = answer.taskAnswerFile;
                        return <div key={answer.id}>
                            {answer.questionContent && <h5>{answer.questionContent}</h5>}
                            {taskAnswerFile.src
                                ? <a href={taskAnswerFile.src} target='_blank'>
                                    {taskAnswerFile.logoSrc && (
                                        <FileLogo src={taskAnswerFile.logoSrc} />
                                    )}
                                    {' '}
                                    {taskAnswerFile.name}
                                </a>
                                : <span>
                                    {taskAnswerFile.logoSrc && (
                                        <FileLogo src={taskAnswerFile.logoSrc} />
                                    )}
                                    {' '}
                                    {taskAnswerFile.name}
                                </span>
                            }
                            {!last && <Hr />}
                        </div>;
                    }
                    return null;
                })}
            </Box>;
        })}
    </>
}

export default UngroupedAnswers;
